import React, { cloneElement, forwardRef } from 'react';
import { styled, Button } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import Box from 'shell/components/Box';
import Stack from 'shell/components/Stack';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipProps } from 'utils/helper';

const COLORS = ['primary', 'neutral', 'destructive'];
const VARIANTS = ['primary', 'secondary', 'tertiary', 'borderless'];

const CustomButton = styled(Button)(({ $color, $disabled, size, variant, theme }) => {
    const minHeight = {
        small: 1.75,
        medium: 2.25,
        large: 3,
    }[size];

    return {
        position: 'relative',
        borderRadius: '4px',
        minWidth: 'unset',
        minHeight: `${minHeight}rem`,
        fontSize: `${theme.typography.button.fontSize[size]}rem`,
        fontWeight: theme.typography.button.fontWeight[variant],
        lineHeight: `${theme.typography.getLineHeight('button', size)}rem`,
        transition: theme.transitions.default,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        ...($disabled && {
            pointerEvents: 'none',
        }),
        ...{
            contained: {
                '&:hover': {
                    background: theme.palette.ensightful.primary[700],
                },
                '&:active': {
                    background: theme.palette.ensightful.primary[800],
                },
                ...($color === 'destructive' && {
                    background: theme.palette.ensightful.error[500],
                    '&:hover': {
                        background: theme.palette.ensightful.error[700],
                    },
                    '&:active': {
                        background: theme.palette.ensightful.error[800],
                    },
                }),
                ...($disabled && {
                    background: theme.palette.ensightful.neutral[300],
                    color: theme.palette.ensightful.neutral[500],
                    '&:active': {
                        background: theme.palette.ensightful.neutral[300],
                    },
                }),
            },
            outlined: {
                borderColor: theme.palette.ensightful.primary[500],
                '&:hover': {
                    borderColor: theme.palette.ensightful.primary[700],
                    background: 'transparent',
                    color: theme.palette.ensightful.primary[700],
                },
                '&:active': {
                    borderColor: theme.palette.ensightful.primary[800],
                    color: theme.palette.ensightful.primary[800],
                },
                ...($color === 'destructive' && {
                    borderColor: theme.palette.ensightful.error[500],
                    color: theme.palette.ensightful.error[500],
                    '&:hover': {
                        borderColor: theme.palette.ensightful.error[700],
                        background: 'transparent',
                        color: theme.palette.ensightful.error[700],
                    },
                    '&:active': {
                        borderColor: theme.palette.ensightful.error[800],
                        color: theme.palette.ensightful.error[800],
                    },
                }),
                ...($disabled && {
                    borderColor: theme.palette.ensightful.neutral[400],
                    background: theme.palette.ensightful.neutral[300],
                    color: theme.palette.ensightful.neutral[500],
                    '&:active': {
                        borderColor: theme.palette.ensightful.neutral[400],
                        color: theme.palette.ensightful.neutral[500],
                    },
                }),
            },
            text: {
                border: '1px solid transparent',
                '&:hover': {
                    borderColor: theme.palette.ensightful.primary[500],
                    background: 'transparent',
                    color: theme.palette.ensightful.primary[500],
                },
                '&:active': {
                    borderColor: theme.palette.ensightful.primary[700],
                    color: theme.palette.ensightful.primary[700],
                },
                ...($color === 'destructive' && {
                    color: theme.palette.ensightful.error[500],
                    '&:hover': {
                        borderColor: theme.palette.ensightful.error[500],
                        background: 'transparent',
                        color: theme.palette.ensightful.error[500],
                    },
                    '&:active': {
                        borderColor: theme.palette.ensightful.error[700],
                        color: theme.palette.ensightful.error[700],
                    },
                }),
                ...($color === 'neutral' && {
                    color: theme.palette.ensightful.neutral[700],
                    '&:hover': {
                        borderColor: theme.palette.ensightful.neutral[700],
                        background: 'transparent',
                        color: theme.palette.ensightful.neutral[700],
                    },
                    '&:active': {
                        borderColor: theme.palette.ensightful.neutral[800],
                        color: theme.palette.ensightful.neutral[800],
                    },
                }),
                ...($disabled && {
                    color: theme.palette.ensightful.neutral[400],
                    '&:active': {
                        color: theme.palette.ensightful.neutral[400],
                    },
                }),
            },
            borderless: {
                minHeight: 'unset',
                padding: 0,
                color: theme.palette.ensightful.primary[500],
                '&:hover': {
                    background: 'transparent',
                    color: theme.palette.ensightful.primary[700],
                },
                '&:active': {
                    color: theme.palette.ensightful.primary[800],
                },
                ...($color === 'destructive' && {
                    color: theme.palette.ensightful.error[500],
                    '&:hover': {
                        background: 'transparent',
                        color: theme.palette.ensightful.error[600],
                    },
                    '&:active': {
                        color: theme.palette.ensightful.error[700],
                    },
                }),
                ...($color === 'neutral' && {
                    color: theme.palette.ensightful.neutral[700],
                    '&:hover': {
                        background: 'transparent',
                        color: theme.palette.ensightful.neutral[800],
                    },
                    '&:active': {
                        color: theme.palette.ensightful.neutral[900],
                    },
                }),
                ...($disabled && {
                    color: theme.palette.ensightful.neutral[400],
                    '&:active': {
                        color: theme.palette.ensightful.neutral[400],
                    },
                }),
            },
        }[variant],
    };
});

const ChildrenBox = styled(Box)(({ $loading, loadingGrow }) => {
    return {
        ...(!loadingGrow && {
            opacity: $loading ? 0 : 1,
        }),
    };
});

const LoadingStack = styled(Stack)(({ $loading, loadingGrow, theme }) => {
    return {
        opacity: $loading ? 1 : 0,
        ...(loadingGrow && {
            marginLeft: theme.spacing(1),
            ...(!$loading && {
                position: 'absolute',
            }),
        }),
        ...(!loadingGrow && {
            position: 'absolute',
        }),
    };
});

const LoadingLeftBox = styled(Box)(() => {
    return {
        animation: 'loading-left 1s ease-out infinite',
        '@keyframes loading-left': {
            '7.5%': {
                transform: 'translate(50%, -15%)',
            },
            '15%': {
                transform: 'translate(100%, -20%)',
            },
            '22.5%': {
                transform: 'translate(175%, -15%)',
            },
            '30%, 100%': {
                transform: 'translate(200%, 0)',
            },
        },
    };
});

const LoadingRightStack = styled(Stack)(() => {
    return {
        animation: 'loading-right 1s ease-in-out infinite',
        '@keyframes loading-right': {
            '30%, 100%': {
                transform: 'translateX(-50%)',
            },
        },
    };
});

const LoadingIndicator = ({ loading, loadingGrow }) => {
    return (
        <LoadingStack
            aria-hidden={!loading}
            direction={'row'}
            $loading={loading}
            loadingGrow={loadingGrow}
            role={'progressbar'}
        >
            <LoadingLeftBox>.</LoadingLeftBox>
            <LoadingRightStack direction={'row'}>..</LoadingRightStack>
        </LoadingStack>
    );
};

export default forwardRef(
    (
        {
            children,
            color = 'primary',
            disabled,
            endIcon,
            loading,
            loadingGrow,
            size,
            startIcon,
            tooltip,
            variant = 'primary',
            onClick,
            ...rest
        },
        ref
    ) => {
        if (tooltip && !tooltip.component)
            throw new Error(
                `Shell: Invalid tooltip in component Button. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
            );
        if (!COLORS.includes(color)) throw new Error(`Shell: Invalid color "${color}" in component Button.`);
        if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component Button.`);

        const { device } = useViewport();

        const mappedSize =
            size ||
            {
                mobile: 'medium',
                tablet: 'medium',
                desktop: 'medium',
            }[device];

        const mappedVariant = {
            primary: 'contained',
            secondary: 'outlined',
            tertiary: 'text',
            borderless: 'borderless',
        }[variant];

        endIcon = endIcon
            ? cloneElement(endIcon, {
                  style: {
                      marginTop: '-0.25rem',
                      marginLeft: '-0.125rem',
                      marginRight: '0.125rem',
                      fontSize: '0.75rem',
                  },
              })
            : undefined;
        startIcon = startIcon
            ? cloneElement(startIcon, {
                  style: {
                      marginTop: '-0.25rem',
                      marginLeft: '0.125rem',
                      marginRight: '-0.125rem',
                      fontSize: '0.75rem',
                  },
              })
            : undefined;

        loading = Boolean(loading);

        const canInteract = !(disabled || loading);

        const ButtonComponent = (
            <CustomButton
                ref={ref}
                aria-disabled={disabled}
                $color={color}
                $disabled={disabled}
                endIcon={endIcon}
                $loading={loading}
                size={mappedSize}
                startIcon={startIcon}
                variant={mappedVariant}
                onClick={canInteract ? onClick : undefined}
                {...rest}
            >
                <ChildrenBox $loading={loading} loadingGrow={loadingGrow}>
                    {children}
                </ChildrenBox>
                <LoadingIndicator loading={loading} loadingGrow={loadingGrow} />
            </CustomButton>
        );

        return tooltip ? (
            <Tooltip {...getTooltipProps(tooltip)}>
                <span>{ButtonComponent}</span>
            </Tooltip>
        ) : (
            ButtonComponent
        );
    }
);
